import React, { useState, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { productsAPI, uploadsAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Storefront, ArrowLeft, CheckCircle, Rocket, HandHeart, ChartLineUp,
  ShoppingBag, Wrench, HandCoins, CurrencyCircleDollar, CalendarBlank,
  Heart, FileArrowDown, Clock, ArrowRight
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { VoiceInputButton } from '../components/VoiceInputButton';
import { ProductImageUpload } from '../components/product/ProductImageUpload';
import { RichTextEditor } from '../components/RichTextEditor';
import PageShell from '../components/PageShell';
import { PillButton } from '../components/PillKit';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

const LISTING_TYPES = [
  { id: 'product', label: 'Product', icon: ShoppingBag, color: '#22C55E', desc: 'Sell a physical or digital product' },
  { id: 'service', label: 'Service', icon: Wrench, color: '#22C55E', desc: 'Offer a one-time service' },
  { id: 'subscription', label: 'Subscription', icon: CalendarBlank, color: '#22C55E', desc: 'Recurring monthly payments' },
  { id: 'crowdfund', label: 'Crowdfund', icon: HandHeart, color: '#22C55E', desc: 'Raise funds with a campaign goal' },
  { id: 'offer', label: 'Make an Offer', icon: HandCoins, color: '#22C55E', desc: 'Let buyers propose their price' },
  { id: 'tip_jar', label: 'Tip Jar', icon: Heart, color: '#22C55E', desc: 'Accept tips of any amount' },
  { id: 'digital_download', label: 'Digital Download', icon: FileArrowDown, color: '#22C55E', desc: 'Sell a downloadable file' },
  { id: 'booking', label: 'Booking', icon: Clock, color: '#22C55E', desc: 'Appointment or time-slot service' },
];

const CATEGORIES = [
  'Art', 'Auto Services', 'Books', 'Clothing', 'Coaching', 'Consulting',
  'Design', 'Development', 'Electronics', 'Food', 'Health & Beauty',
  'Jewelry', 'Marketing', 'Music', 'Musical Instruments', 'Photography',
  'Teaching', 'Toys & Games', 'Transportation', 'Video', 'Writing', 'Other'
];

const FUNDING_REASONS = [
  'Launch a Business', 'Creative Project', 'Community Cause', 'Personal Goal',
  'Emergency / Medical', 'Education', 'Event', 'Other'
];

const PREMADE_AVATARS = {
  Other: [
    { id: 'other-1', svg: <svg viewBox="0 0 80 80" className="w-full h-full"><rect width="80" height="80" rx="4" fill="#111111"/><rect x="24" y="24" width="32" height="32" rx="4" stroke="#22C55E" strokeWidth="2" fill="#22C55E" opacity="0.1"/><path d="M36 36h8M40 32v8" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round"/></svg> },
  ],
};
const DEFAULT_AVATARS = PREMADE_AVATARS.Other;

export default function ProductFormPage() {
  const navigate = useNavigate();
  const { productId } = useParams();
  const isEdit = !!productId;
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(isEdit ? 'form' : 'type');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingDigitalFile, setUploadingDigitalFile] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', price: '', category: '', image_url: '', images: [], available: true,
    listing_type: 'product',
    is_crowdfund: false, funding_goal: '', funding_reason: '',
    investment_enabled: false, investment_amount: '', investment_percentage: '',
    subscription_interval: 'monthly', min_commitment_months: '3',
    min_offer_amount: '', download_url: '',
    digital_file: null
  });

  React.useEffect(() => { if (formData.image_url) setPreviewUrl(formData.image_url); }, [formData.image_url]);

  const loadProduct = React.useCallback(async () => {
    try {
      const response = await productsAPI.get(productId);
      const p = response.data.product;
      setFormData({
        name: p.name || '', description: p.description || '', price: p.price?.toString() || '',
        category: p.category || '', image_url: p.image_url || '',
        images: p.images || (p.image_url ? [p.image_url] : []), available: p.available !== false,
        listing_type: p.listing_type || 'product',
        is_crowdfund: p.is_crowdfund || false, funding_goal: p.funding_goal?.toString() || '',
        funding_reason: p.funding_reason || '',
        investment_enabled: p.investment_enabled || false, investment_amount: p.investment_amount?.toString() || '',
        investment_percentage: p.investment_percentage?.toString() || '',
        subscription_interval: p.subscription_interval || 'monthly',
        min_commitment_months: p.min_commitment_months?.toString() || '3',
        min_offer_amount: p.min_offer_amount?.toString() || '',
        download_url: p.download_url || '',
        digital_file: p.digital_file || null
      });
    } catch (error) { toast.error(formatApiError(error)); navigate('/profile'); }
  }, [productId, navigate]);

  React.useEffect(() => { if (isEdit) { loadProduct(); } }, [isEdit, loadProduct]);

  const selectType = (typeId) => {
    const updates = { listing_type: typeId, is_crowdfund: false };
    if (typeId === 'crowdfund') updates.is_crowdfund = true;
    setFormData(prev => ({ ...prev, ...updates }));
    setStep('form');
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const handleDigitalFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 500 * 1024 * 1024) {
      toast.error('File must be under 500MB');
      return;
    }
    setUploadingDigitalFile(true);
    try {
      const { data } = await uploadsAPI.uploadDigitalFile(file);
      setFormData(prev => ({ ...prev, digital_file: data }));
      toast.success(`Uploaded ${data.filename}`);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setUploadingDigitalFile(false);
    }
  };

  const removeDigitalFile = () => {
    setFormData(prev => ({ ...prev, digital_file: null }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setPreviewUrl(URL.createObjectURL(file));
    setUploading(true);
    try {
      const res = await uploadsAPI.uploadProductImage(file);
      const imageUrl = res.data.image_url;
      setFormData(prev => ({ ...prev, image_url: prev.image_url || imageUrl, images: [...(prev.images || []), imageUrl] }));
      setPreviewUrl(imageUrl);
      toast.success('Image uploaded!');
    } catch (error) { toast.error(formatApiError(error)); setPreviewUrl(null); }
    finally { setUploading(false); }
  };

  const selectAvatar = (avatarId) => {
    const svgEl = document.getElementById(`avatar-${avatarId}`);
    if (svgEl) {
      const svgData = new XMLSerializer().serializeToString(svgEl);
      const dataUrl = `data:image/svg+xml;base64,${btoa(svgData)}`;
      setFormData({ ...formData, image_url: dataUrl });
      setPreviewUrl(dataUrl);
    }
    setShowAvatarPicker(false);
    toast.success('Avatar selected!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.description || !formData.category) { toast.error('Please fill in all required fields'); return; }
    if (formData.listing_type !== 'tip_jar' && formData.listing_type !== 'offer') {
      const parsedPrice = parseFloat(formData.price);
      if (isNaN(parsedPrice) || parsedPrice <= 0) { toast.error('Please enter a valid price'); return; }
    }

    setLoading(true);
    try {
      const productData = { ...formData, price: parseFloat(formData.price) || 0 };
      // Clean empty strings from numeric fields
      ['funding_goal', 'investment_amount', 'investment_percentage', 'min_offer_amount', 'min_commitment_months'].forEach(f => {
        if (productData[f] === '' || productData[f] === undefined) productData[f] = null;
      });
      if (formData.listing_type === 'crowdfund' && formData.funding_goal) {
        productData.funding_goal = parseFloat(formData.funding_goal);
      }
      if (formData.investment_enabled && formData.investment_amount && formData.investment_percentage) {
        productData.investment_amount = parseFloat(formData.investment_amount);
        productData.investment_percentage = parseFloat(formData.investment_percentage);
        if (productData.investment_percentage > 50) { toast.error('Max 50% per investor'); setLoading(false); return; }
      } else {
        productData.investment_enabled = false;
        productData.investment_amount = null;
        productData.investment_percentage = null;
      }
      if (formData.listing_type === 'subscription') {
        productData.min_commitment_months = parseInt(formData.min_commitment_months) || 3;
      }
      if (formData.listing_type === 'offer' && formData.min_offer_amount) {
        productData.min_offer_amount = parseFloat(formData.min_offer_amount);
      }

      if (isEdit) { await productsAPI.update(productId, productData); toast.success('Updated!'); }
      else { await productsAPI.create(productData); setSuccess(true); toast.success('Created!'); }
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setLoading(false); }
  };

  const currentType = LISTING_TYPES.find(t => t.id === formData.listing_type) || LISTING_TYPES[0];
  const avatarsForCategory = DEFAULT_AVATARS;

  // Success Screen
  if (success) {
    return (
      <PageShell testId="product-success" back>
        <div className="max-w-md mx-auto bg-black border-[3px] border-[#22C55E] rounded-[24px] p-8 text-center mt-6" data-testid="product-success-card">
          <div className="w-20 h-20 rounded-full border-[3px] border-[#22C55E] flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-[#22C55E]" weight="fill" />
          </div>
          <h2 className="text-3xl font-extrabold tracking-tight text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>Listing Added!</h2>
          <p className="text-white/60 mb-8">Your {currentType.label.toLowerCase()} is now live.</p>
          <div className="space-y-3">
            <PillButton onClick={() => { setSuccess(false); setStep('type'); setFormData(prev => ({ ...prev, name: '', description: '', price: '', image_url: '', images: [] })); setPreviewUrl(null); }} data-testid="add-another-button">
              Add Another
            </PillButton>
            <Link to="/profile">
              <PillButton variant="white" data-testid="view-products-button">View Your Listings</PillButton>
            </Link>
          </div>
        </div>
      </PageShell>
    );
  }

  // Type Selector Screen
  if (step === 'type') {
    return (
      <PageShell testId="listing-type-selector" back title={{ line1: 'What are you', line2: 'listing?' }}>
        <p className="text-white/60 text-sm mb-8 -mt-2">Choose the type that best fits what you&apos;re selling.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {LISTING_TYPES.map((type) => (
            <button
              key={type.id}
              onClick={() => selectType(type.id)}
              className="bg-black border-[3px] border-white rounded-[18px] p-4 text-left active:scale-[0.97] transition-transform aspect-square flex flex-col"
              data-testid={`type-${type.id}`}
            >
              <div className="w-12 h-12 rounded-full bg-[#22C55E]/15 flex items-center justify-center mb-3">
                <type.icon className="w-7 h-7 text-[#22C55E]" weight="fill" />
              </div>
              <h3 className="text-white font-extrabold text-sm leading-tight mb-1" style={{ fontFamily: 'Cabinet Grotesk' }}>{type.label}</h3>
              <p className="text-white/50 text-[11px] leading-snug flex-1">{type.desc}</p>
            </button>
          ))}
        </div>
      </PageShell>
    );
  }

  // Form Screen
  const priceLabel = formData.listing_type === 'subscription' ? 'Monthly Price' :
    formData.listing_type === 'tip_jar' ? 'Suggested Tip' :
    formData.listing_type === 'offer' ? 'Suggested Price' :
    formData.listing_type === 'crowdfund' ? 'Suggested Contribution' :
    formData.listing_type === 'booking' ? 'Session Price' : 'Price';

  return (
    <PageShell testId="product-form-page" back>
      <div className="max-w-lg mx-auto">
        {!isEdit && (
          <button onClick={() => setStep('type')} className="inline-flex items-center text-sm text-white/60 hover:text-white mb-4" data-testid="back-to-type-selector">
            <ArrowLeft className="w-4 h-4 mr-2" />Change type
          </button>
        )}
        <div className="mb-6 flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#22C55E]/15 flex items-center justify-center flex-shrink-0">
            <currentType.icon className="w-7 h-7 text-[#22C55E]" weight="fill" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-white leading-none" style={{ fontFamily: 'Cabinet Grotesk' }}>
              {isEdit ? 'Edit' : 'Add'} {currentType.label}
            </h1>
            <p className="text-[#22C55E] text-sm font-bold mt-1">{currentType.desc}</p>
          </div>
        </div>

        <Card className="bg-black border-[3px] border-white rounded-[18px] shadow-none" data-testid="product-form-card">
          <CardHeader className="p-6 sm:p-8 pb-4 sm:pb-6 hidden">
            {!isEdit && (
              <button onClick={() => setStep('type')} className="inline-flex items-center text-sm text-[#999999] hover:text-white mb-4" data-testid="back-to-type-selector-hidden">
                <ArrowLeft className="w-4 h-4 mr-2" />Change type
              </button>
            )}
            <CardTitle className="text-2xl font-bold tracking-tight text-white flex items-center gap-3" style={{ fontFamily: 'Cabinet Grotesk' }}>
              <div className="w-10 h-10 rounded-sm flex items-center justify-center" style={{ backgroundColor: `${currentType.color}15` }}>
                <currentType.icon className="w-5 h-5" style={{ color: currentType.color }} weight="duotone" />
              </div>
              {isEdit ? `Edit ${currentType.label}` : `New ${currentType.label}`}
            </CardTitle>
            <CardDescription className="text-[#999999]">{currentType.desc}</CardDescription>
          </CardHeader>
          <CardContent className="p-6 sm:p-8 pt-0">
            <form onSubmit={handleSubmit} className="space-y-5">
              <ProductImageUpload formData={formData} setFormData={setFormData} previewUrl={previewUrl} setPreviewUrl={setPreviewUrl} uploading={uploading} onFileUpload={handleFileUpload} showAvatarPicker={showAvatarPicker} setShowAvatarPicker={setShowAvatarPicker} onSelectAvatar={selectAvatar} avatarsForCategory={avatarsForCategory} fileInputRef={fileInputRef} />

              <div className="space-y-2">
                <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Name *</Label>
                <div className="flex gap-2">
                  <Input type="text" placeholder="e.g., Logo Design Package" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="rounded-sm bg-[#111111] border-[#333333] text-white placeholder:text-[#666666] flex-1" data-testid="product-name-input" />
                  <VoiceInputButton onTranscript={(text) => setFormData(prev => ({ ...prev, name: prev.name ? prev.name + ' ' + text : text }))} />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Description *</Label>
                <RichTextEditor
                  value={formData.description}
                  onChange={(val) => setFormData(prev => ({ ...prev, description: val }))}
                  placeholder="Describe what you're offering..."
                />
                <div className="flex justify-end">
                  <VoiceInputButton onTranscript={(text) => setFormData(prev => ({ ...prev, description: prev.description ? prev.description + ' ' + text : text }))} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">{priceLabel} {formData.listing_type !== 'tip_jar' && formData.listing_type !== 'offer' ? '*' : ''}</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999999] font-medium">$</span>
                    <Input type="number" step="0.01" min="0" placeholder="0.00" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} className="rounded-sm bg-[#111111] border-[#333333] text-white pl-8" data-testid="product-price-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Category *</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                    <SelectTrigger className="rounded-sm bg-[#111111] border-[#333333] text-white" data-testid="product-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="rounded-sm bg-[#1A1A1A] border-[#333333]">
                      {CATEGORIES.map((cat) => <SelectItem key={cat} value={cat} className="text-white hover:bg-[#333333]">{cat}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Crowdfund fields */}
              {formData.listing_type === 'crowdfund' && (
                <div className="space-y-4 p-4 bg-[#0D1F17] border border-[#22C55E]/20 rounded-sm">
                  <div className="flex items-center gap-2 mb-1"><Rocket className="w-4 h-4 text-[#22C55E]" /><span className="text-sm font-semibold text-[#22C55E]">Campaign Details</span></div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Funding Goal *</Label>
                    <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999999]">$</span><Input type="number" step="1" min="1" placeholder="5000" value={formData.funding_goal} onChange={(e) => setFormData({ ...formData, funding_goal: e.target.value })} className="rounded-sm bg-[#111111] border-[#333333] text-white pl-8" data-testid="funding-goal-input" /></div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Campaign Reason *</Label>
                    <Select value={formData.funding_reason} onValueChange={(value) => setFormData({ ...formData, funding_reason: value })}>
                      <SelectTrigger className="rounded-sm bg-[#111111] border-[#333333] text-white" data-testid="funding-reason-select"><SelectValue placeholder="Why are you crowdfunding?" /></SelectTrigger>
                      <SelectContent className="rounded-sm bg-[#1A1A1A] border-[#333333]">{FUNDING_REASONS.map((r) => <SelectItem key={r} value={r} className="text-white hover:bg-[#333333]">{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Offer fields */}
              {formData.listing_type === 'offer' && (
                <div className="space-y-4 p-4 bg-[#0D1F17] border border-[#22C55E]/20 rounded-sm">
                  <div className="flex items-center gap-2 mb-1"><HandCoins className="w-4 h-4 text-[#22C55E]" /><span className="text-sm font-semibold text-[#22C55E]">Offer Settings</span></div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Minimum Offer Amount</Label>
                    <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999999]">$</span><Input type="number" step="1" min="0" placeholder="Optional" value={formData.min_offer_amount} onChange={(e) => setFormData({ ...formData, min_offer_amount: e.target.value })} className="rounded-sm bg-[#111111] border-[#333333] text-white pl-8" data-testid="min-offer-input" /></div>
                  </div>
                  <p className="text-[10px] text-[#666666]">Fans and followers make their own offer. You review and accept or decline each one.</p>
                </div>
              )}

              {/* Subscription fields */}
              {formData.listing_type === 'subscription' && (
                <div className="space-y-4 p-4 bg-[#0D1F17] border border-[#22C55E]/20 rounded-sm">
                  <div className="flex items-center gap-2 mb-1"><CalendarBlank className="w-4 h-4 text-[#22C55E]" /><span className="text-sm font-semibold text-[#22C55E]">Subscription Details</span></div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Minimum Commitment *</Label>
                    <Select value={formData.min_commitment_months} onValueChange={(v) => setFormData({ ...formData, min_commitment_months: v })}>
                      <SelectTrigger className="rounded-sm bg-[#111111] border-[#333333] text-white" data-testid="commitment-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-sm bg-[#1A1A1A] border-[#333333]">
                        <SelectItem value="3" className="text-white hover:bg-[#333333]">3 months</SelectItem>
                        <SelectItem value="6" className="text-white hover:bg-[#333333]">6 months</SelectItem>
                        <SelectItem value="12" className="text-white hover:bg-[#333333]">12 months</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-[10px] text-[#666666]">Subscribers pay monthly. Minimum commitment ensures stability for your business.</p>
                </div>
              )}

              {/* Digital Download field */}
              {formData.listing_type === 'digital_download' && (
                <div className="space-y-4 p-4 bg-[#0D1F17] border border-[#22C55E]/20 rounded-sm">
                  <div className="flex items-center gap-2 mb-1"><FileArrowDown className="w-4 h-4 text-[#22C55E]" /><span className="text-sm font-semibold text-[#22C55E]">Digital File</span></div>

                  {formData.digital_file ? (
                    <div className="flex items-start justify-between gap-3 p-3 bg-[#111111] border border-[#22C55E]/30 rounded-sm" data-testid="digital-file-attached">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <FileArrowDown className="w-8 h-8 text-[#22C55E] shrink-0 mt-0.5" weight="duotone" />
                        <div className="min-w-0">
                          <p className="text-sm text-white font-medium truncate" data-testid="digital-file-name">{formData.digital_file.filename}</p>
                          <p className="text-xs text-[#999999]">{formatFileSize(formData.digital_file.size)} · stored privately</p>
                          <p className="text-[10px] text-[#666666] mt-1">Buyers will be able to download this file from their library after purchase.</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeDigitalFile}
                        className="text-[10px] text-[#FF3B30] hover:underline shrink-0 font-semibold uppercase tracking-wider"
                        data-testid="remove-digital-file"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <label
                      htmlFor="digital-file-upload"
                      className={`block border-2 border-dashed rounded-sm p-6 text-center cursor-pointer transition-colors ${uploadingDigitalFile ? 'border-[#22C55E] bg-[#22C55E]/5' : 'border-[#333333] hover:border-[#22C55E] hover:bg-[#22C55E]/5'}`}
                      data-testid="digital-file-dropzone"
                    >
                      <FileArrowDown className="w-8 h-8 text-[#22C55E] mx-auto mb-2" weight="duotone" />
                      <p className="text-sm text-white font-medium mb-1">
                        {uploadingDigitalFile ? 'Uploading…' : 'Click to upload your digital file'}
                      </p>
                      <p className="text-xs text-[#999999]">PDFs, ZIPs, MP3s, MP4s, etc · max 500MB</p>
                      <input
                        id="digital-file-upload"
                        type="file"
                        className="hidden"
                        onChange={handleDigitalFileUpload}
                        disabled={uploadingDigitalFile}
                        data-testid="digital-file-input"
                      />
                    </label>
                  )}

                  <div className="space-y-2 pt-2">
                    <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">External link (optional)</Label>
                    <Input
                      type="url"
                      placeholder="https://drive.google.com/... (used as fallback if no file uploaded)"
                      value={formData.download_url}
                      onChange={(e) => setFormData({ ...formData, download_url: e.target.value })}
                      className="rounded-sm bg-[#111111] border-[#333333] text-white placeholder:text-[#666666]"
                      data-testid="download-url-input"
                    />
                    <p className="text-[10px] text-[#666666]">Use this only if your file is too large to upload. Hosted files (above) are more reliable.</p>
                  </div>
                </div>
              )}

              {/* DiviSpend Toggle — available on all listing types */}
              <div className="flex items-center justify-between p-4 bg-[#111111] rounded-sm border border-[#333333]">
                <div className="flex items-center gap-3">
                  <ChartLineUp className="w-5 h-5 text-[#22C55E]" />
                  <div><Label className="text-sm font-medium text-white">DiviSpend</Label><p className="text-xs text-[#999999]">Open this listing for investor support</p></div>
                </div>
                <Switch checked={formData.investment_enabled} onCheckedChange={(checked) => setFormData({ ...formData, investment_enabled: checked })} data-testid="investment-toggle" />
              </div>

              {formData.investment_enabled && (
                <div className="space-y-4 p-4 bg-[#0D1F17] border border-[#22C55E]/20 rounded-sm">
                  <div className="flex items-center gap-2 mb-1"><ChartLineUp className="w-4 h-4 text-[#22C55E]" /><span className="text-sm font-semibold text-[#22C55E]">DiviSpend Investment Terms</span></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Investment Amount *</Label>
                      <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999999]">$</span><Input type="number" step="1" min="1" placeholder="200" value={formData.investment_amount} onChange={(e) => setFormData({ ...formData, investment_amount: e.target.value })} className="rounded-sm bg-[#111111] border-[#333333] text-white pl-8" data-testid="investment-amount-input" /></div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Revenue Share % *</Label>
                      <div className="relative"><Input type="number" step="1" min="1" max="50" placeholder="10" value={formData.investment_percentage} onChange={(e) => setFormData({ ...formData, investment_percentage: e.target.value })} className="rounded-sm bg-[#111111] border-[#333333] text-white pr-8" data-testid="investment-percentage-input" /><span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999999]">%</span></div>
                    </div>
                  </div>
                  <p className="text-[10px] text-[#666666]">Investors earn their % of each sale until they recoup their investment. Max 50% total.</p>
                </div>
              )}

              {/* Available toggle */}
              <div className="flex items-center justify-between p-4 bg-[#111111] rounded-sm border border-[#333333]">
                <div><Label className="text-sm font-medium text-white">Available</Label><p className="text-xs text-[#999999]">Show this listing publicly</p></div>
                <Switch checked={formData.available} onCheckedChange={(checked) => setFormData({ ...formData, available: checked })} data-testid="product-available-switch" />
              </div>

              <Button type="submit" disabled={loading} className="w-full rounded-full h-12 font-bold text-black bg-[#22C55E] hover:bg-[#16A34A] border-[3px] border-[#22C55E]" data-testid="submit-product-button">
                {loading ? 'Saving...' : (isEdit ? `Update ${currentType.label}` : `Add ${currentType.label}`)}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </PageShell>
  );
}
